import React, { useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Grid,
  Switch,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { PortalLoadingCard, PortalStateCard } from './portalShared'

const DEFAULT_PREFS = {
  pushEnabled: true,
  smsEnabled: false,
  emailEnabled: false,
  level: 'all_updates',
  directMessages: true,
  crewMessages: true,
  vendorMessages: true,
  jobMessages: true,
  dailySummary: false,
  weeklySummary: true
}

const LEVEL_OPTIONS = [
  {
    value: 'urgent_only',
    label: 'Urgent only',
    description: 'Priority alerts and critical changes only.'
  },
  {
    value: 'messages_only',
    label: 'Messages only',
    description: 'Only direct, crew, and vendor message activity.'
  },
  {
    value: 'all_updates',
    label: 'All updates',
    description: 'Messages and operational updates.'
  }
]

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: '1px solid rgba(205, 216, 209, 0.9)'
  },
  cardTitle: {
    fontWeight: 700,
    color: '#20342b'
  },
  helperText: {
    color: '#5d6c64'
  },
  toggleCard: {
    borderRadius: 8,
    border: '1px solid #dce6df',
    padding: theme.spacing(2),
    height: '100%'
  },
  toggleTitle: {
    fontWeight: 700,
    color: '#20342b'
  },
  toggleDescription: {
    color: '#5d6c64',
    marginTop: theme.spacing(0.5)
  },
  actionRow: {
    display: 'flex',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(3)
  },
  sectionTitle: {
    fontWeight: 700,
    color: '#20342b',
    marginBottom: theme.spacing(1)
  }
}))

function buildChannelCards (prefs, onToggle) {
  return [
    {
      key: 'pushEnabled',
      title: 'Push notifications',
      description: 'Send device notifications when message activity matches your settings.',
      enabled: prefs.pushEnabled,
      onToggle: (event) => onToggle('pushEnabled', event.target.checked)
    },
    {
      key: 'smsEnabled',
      title: 'SMS escalation',
      description: 'Receive text message alerts for qualifying notifications.',
      enabled: prefs.smsEnabled,
      onToggle: (event) => onToggle('smsEnabled', event.target.checked)
    },
    {
      key: 'emailEnabled',
      title: 'Email notifications',
      description: 'Receive email alerts and summaries.',
      enabled: prefs.emailEnabled,
      onToggle: (event) => onToggle('emailEnabled', event.target.checked)
    }
  ]
}

function buildThreadTypeCards (prefs, onToggle) {
  return [
    {
      key: 'directMessages',
      title: 'Direct conversations',
      description: '1:1 threads targeted to you.',
      enabled: prefs.directMessages,
      onToggle: (event) => onToggle('directMessages', event.target.checked)
    },
    {
      key: 'crewMessages',
      title: 'Crew conversations',
      description: 'Messages sent to your assigned crew.',
      enabled: prefs.crewMessages,
      onToggle: (event) => onToggle('crewMessages', event.target.checked)
    },
    {
      key: 'vendorMessages',
      title: 'Vendor-wide conversations',
      description: 'Company-wide announcements and discussions.',
      enabled: prefs.vendorMessages,
      onToggle: (event) => onToggle('vendorMessages', event.target.checked)
    },
    {
      key: 'jobMessages',
      title: 'Job conversations',
      description: 'Threads tied to a specific job or work order.',
      enabled: prefs.jobMessages,
      onToggle: (event) => onToggle('jobMessages', event.target.checked)
    }
  ]
}

const SubcontractorNotifications = ({ notification }) => {
  const classes = useStyles()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [prefs, setPrefs] = useState(DEFAULT_PREFS)

  const loadPrefs = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const response = await axios.get('/api/subcontractor/v2/notification-prefs')
      setPrefs({
        ...DEFAULT_PREFS,
        ...(response.data?.prefs || {})
      })
    } catch (requestError) {
      console.error('Failed to load subcontractor notification preferences', requestError)
      setError(requestError?.response?.data?.error || 'Unable to load notification preferences right now.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadPrefs()
  }, [loadPrefs])

  const updatePref = (key, value) => {
    setPrefs((current) => ({
      ...current,
      [key]: value
    }))
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const response = await axios.put('/api/subcontractor/v2/notification-prefs', { prefs })
      setPrefs({
        ...DEFAULT_PREFS,
        ...(response.data?.prefs || {})
      })
      notification?.({
        type: 'success',
        title: 'Preferences Saved',
        message: 'Notification preferences updated for your subcontractor account.'
      })
    } catch (requestError) {
      console.error('Failed to save subcontractor notification preferences', requestError)
      notification?.({
        type: 'danger',
        title: 'Save Failed',
        message: requestError?.response?.data?.error || 'Unable to save notification preferences.'
      })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <PortalLoadingCard title="Loading notifications" message="Loading your notification settings..." minHeight={260} />
  }

  if (error) {
    return <PortalStateCard title="Notifications unavailable" message={error} actionLabel="Retry" onAction={loadPrefs} tone="danger" minHeight={260} />
  }

  return (
    <Card className={classes.card}>
      <CardContent>
        <Typography variant="h6" className={classes.cardTitle}>Notification Preferences</Typography>
        <Typography variant="body2" className={classes.helperText}>
          Choose how you want to be notified about new messages and updates.
        </Typography>

        <Box mt={3}>
          <Typography variant="subtitle1" className={classes.sectionTitle}>Delivery Channels</Typography>
          <Grid container spacing={2}>
            {buildChannelCards(prefs, updatePref).map((item) => (
              <Grid item xs={12} md={4} key={item.key}>
                <div className={classes.toggleCard}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" gridGap={12}>
                    <div>
                      <Typography variant="subtitle2" className={classes.toggleTitle}>{item.title}</Typography>
                      <Typography variant="body2" className={classes.toggleDescription}>{item.description}</Typography>
                    </div>
                    <Switch color="primary" checked={item.enabled} onChange={item.onToggle} />
                  </Box>
                </div>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box mt={3}>
          <Typography variant="subtitle1" className={classes.sectionTitle}>Message Types</Typography>
          <Grid container spacing={2}>
            {buildThreadTypeCards(prefs, updatePref).map((item) => (
              <Grid item xs={12} md={6} key={item.key}>
                <div className={classes.toggleCard}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" gridGap={12}>
                    <div>
                      <Typography variant="subtitle2" className={classes.toggleTitle}>{item.title}</Typography>
                      <Typography variant="body2" className={classes.toggleDescription}>{item.description}</Typography>
                    </div>
                    <Switch color="primary" checked={item.enabled} onChange={item.onToggle} />
                  </Box>
                </div>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box mt={3}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <div className={classes.toggleCard}>
                <Typography variant="subtitle2" className={classes.toggleTitle}>Notification level</Typography>
                <Typography variant="body2" className={classes.toggleDescription}>
                  Set how often you receive notifications.
                </Typography>
                <Box mt={2}>
                  <Typography variant="body2" className={classes.toggleDescription}>
                    Current setting: {LEVEL_OPTIONS.find(option => option.value === prefs.level)?.label || 'All updates'}
                  </Typography>
                </Box>
                <Box mt={2}>
                  {LEVEL_OPTIONS.map((option) => (
                    <Button
                      key={option.value}
                      variant={prefs.level === option.value ? 'contained' : 'outlined'}
                      color="primary"
                      onClick={() => updatePref('level', option.value)}
                      style={{ marginRight: 8, marginBottom: 8, textTransform: 'none' }}
                    >
                      {option.label}
                    </Button>
                  ))}
                  <Typography variant="body2" className={classes.toggleDescription}>
                    {LEVEL_OPTIONS.find(option => option.value === prefs.level)?.description}
                  </Typography>
                </Box>
              </div>
            </Grid>
            <Grid item xs={12} md={6}>
              <div className={classes.toggleCard}>
                <Typography variant="subtitle2" className={classes.toggleTitle}>Summaries</Typography>
                <Typography variant="body2" className={classes.toggleDescription}>
                  Get a digest of recent activity delivered to you on a schedule.
                </Typography>
                <Box mt={2}>
                  <FormControlLabel
                    control={<Switch color="primary" checked={prefs.dailySummary} onChange={(event) => updatePref('dailySummary', event.target.checked)} />}
                    label="Daily summary"
                  />
                </Box>
                <Box>
                  <FormControlLabel
                    control={<Switch color="primary" checked={prefs.weeklySummary} onChange={(event) => updatePref('weeklySummary', event.target.checked)} />}
                    label="Weekly summary"
                  />
                </Box>
              </div>
            </Grid>
          </Grid>
        </Box>

        <div className={classes.actionRow}>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Preferences'}
          </Button>
          <Button variant="outlined" onClick={loadPrefs} disabled={saving}>
            Reload
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

const mapStateToProps = (state) => ({
  notification: state.notification
})

export default connect(mapStateToProps)(SubcontractorNotifications)
